import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CLIENTS } from "@/lib/media";

// Uploaded testimonial cards — portrait + quote + client + metric are baked into each PNG.
// Alt-text carries the underlying quote for accessibility.
const CARDS = [
    {
        id: "hks",
        img: CLIENTS.hks.testimonialCard,
        name: "Mr. Surjit Singh",
        role: "Director — India Operations",
        client: CLIENTS.hks.name,
        metric: "150% ↑ Quality Leads",
        window: "6 months",
        alt: "Testimonial from Mr. Surjit Singh, Director India Operations at HKS 1835 — 150% increase in quality leads in 6 months.",
    },
    {
        id: "karnani",
        img: CLIENTS.karnani.testimonialCard,
        name: "Mr. Anup Karnani",
        role: "Managing Director",
        client: "Karnani Builders",
        metric: "200% ↑ Qualified Leads",
        window: "6 months",
        alt: "Testimonial from Mr. Anup Karnani, MD Karnani Builders — 200% increase in qualified leads in 6 months.",
    },
    {
        id: "logoinfosoft",
        img: CLIENTS.logoinfosoft.testimonialCard,
        name: "Sumanta Chowdhury",
        role: "CMO",
        client: CLIENTS.logoinfosoft.name,
        metric: "300% ↑ Qualified Leads",
        window: "6 months",
        alt: "Testimonial from Sumanta Chowdhury, CMO Logoinfosoft — 300% increase in qualified leads in 6 months.",
    },
    {
        id: "scf",
        img: CLIENTS.scf.testimonialCard,
        name: "Ms. Firoza Dadan",
        role: "Founder",
        client: CLIENTS.scf.name,
        metric: "3× Engagement",
        window: "across campaigns",
        alt: "Testimonial from Ms. Firoza Dadan, Founder Smart Commute Foundation — 3× engagement across campaigns.",
    },
    {
        id: "sbca",
        img: CLIENTS.sbca.testimonialCard,
        name: "Sushil Setia",
        role: "Founder",
        client: "School of Bakery & Culinary Art",
        metric: "200% ↑ Enrolments",
        window: "6 months",
        alt: "Testimonial from Sushil Setia, Founder School of Bakery & Culinary Art — 200% increase in enrolments in 6 months.",
    },
];

const ease = [0.22, 1, 0.36, 1];

export default function Testimonials() {
    const [idx, setIdx] = useState(0);
    const current = CARDS[idx];

    return (
        <section
            id="testimonials"
            data-testid="testimonials-section"
            className="relative py-24 lg:py-32 bg-[#F5EFE1]"
        >
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14 lg:mb-20">
                    <div>
                        <div className="text-xs uppercase tracking-[0.22em] font-bold text-black/55">
                            (What partners say / 06)
                        </div>
                        <h2 className="mt-4 font-display font-black uppercase tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-7xl">
                            In their own
                            <br />
                            <span className="text-olive">words.</span>
                        </h2>
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-black/50">
                        {String(idx + 1).padStart(2, "0")}{" "}
                        <span className="text-black/30">
                            / {String(CARDS.length).padStart(2, "0")}
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-y-10 lg:gap-x-12 items-start">
                    {/* Card image */}
                    <div className="col-span-12 lg:col-span-8">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={current.id}
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.7, ease }}
                                data-testid={`testimonial-card-${current.id}`}
                                className="relative overflow-hidden shadow-[0_30px_60px_-30px_rgba(0,0,0,0.25)] bg-white"
                            >
                                <img
                                    src={current.img}
                                    alt={current.alt}
                                    className="w-full h-auto object-contain"
                                    loading="lazy"
                                />
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Meta rail */}
                    <div className="col-span-12 lg:col-span-4 lg:pl-4 lg:pt-4">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={current.id + "-meta"}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5, ease }}
                            >
                                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/50">
                                    Client
                                </div>
                                <div className="mt-2 font-display font-bold text-2xl lg:text-3xl tracking-tight">
                                    {current.client}
                                </div>

                                <div className="mt-8 pt-6 border-t border-black/15">
                                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/50">
                                        Impact
                                    </div>
                                    <div
                                        data-testid="testimonial-metric"
                                        className="mt-2 font-display font-black text-3xl lg:text-4xl tracking-tight text-[#2E5019]"
                                    >
                                        {current.metric}
                                    </div>
                                    <div className="mt-1 text-[11px] uppercase tracking-[0.2em] font-bold text-black/55">
                                        in {current.window}
                                    </div>
                                </div>

                                <div className="mt-8 pt-6 border-t border-black/15">
                                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/50">
                                        Signed
                                    </div>
                                    <div className="mt-2 font-display font-bold text-lg tracking-tight">
                                        {current.name}
                                    </div>
                                    <div className="text-sm text-black/60">
                                        {current.role}
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>

                {/* Controls */}
                <div className="mt-14 flex flex-wrap items-center justify-between gap-6 border-t border-black/15 pt-6">
                    <div className="flex items-center gap-2">
                        {CARDS.map((c, i) => (
                            <button
                                key={c.id}
                                data-testid={`testimonial-dot-${i}`}
                                aria-label={`Show testimonial ${i + 1}`}
                                onClick={() => setIdx(i)}
                                className={`h-2 transition-all duration-500 ${
                                    i === idx
                                        ? "w-10 bg-[#2E5019]"
                                        : "w-3 bg-black/25 hover:bg-black/50"
                                }`}
                            />
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="testimonial-prev"
                            onClick={() =>
                                setIdx((i) =>
                                    i === 0 ? CARDS.length - 1 : i - 1,
                                )
                            }
                            className="w-10 h-10 border border-black/20 flex items-center justify-center hover:bg-olive hover:text-bone hover:border-olive transition-colors"
                            aria-label="Previous testimonial"
                        >
                            ←
                        </button>
                        <button
                            data-testid="testimonial-next"
                            onClick={() =>
                                setIdx((i) =>
                                    i === CARDS.length - 1 ? 0 : i + 1,
                                )
                            }
                            className="w-10 h-10 border border-black/20 flex items-center justify-center hover:bg-olive hover:text-bone hover:border-olive transition-colors"
                            aria-label="Next testimonial"
                        >
                            →
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
